/* eslint-disable no-unsafe-optional-chaining */
import { FunctionComponent } from 'react';
import dayjs from 'dayjs';
import Link from 'next/link';

import { Routes } from 'types/routes';

import { JobAgreementsLabels } from 'models/JobAgreement';

import { isBrowser } from 'utils/browser';
import { imageResizer } from 'utils/image';
import { withVariables } from 'utils/string';
import Tracking, { SmEventDescriptons, SmEventTypes } from 'services/Tracking';

import Button, { ButtonElements, ButtonStyles, ButtonVariants } from 'components/layout/Button';
import SaveButton from 'components/modules/public/layout/controls/SaveButton';

import { Props } from './index';
import StyledComponent from './styles';

const PublicLayoutJobsResultsListElement: FunctionComponent<Props> = ({
    job,
    buttonProps,
    jobCity,
    userEmail,
}) => {
    const getCreatedAtLabel = (value?: string | number | null): string => {
        if (value === undefined || value === null || value === '') return '';

        const parseDate = (rawValue: string | number) => {
            const parsed = dayjs(rawValue);
            if (parsed.isValid()) return parsed;

            const numericValue = Number(rawValue);
            if (!Number.isNaN(numericValue)) {
                const timestamp = numericValue > 1e12 ? numericValue : numericValue * 1000;
                return dayjs(timestamp);
            }

            return null;
        };

        const createdAt = parseDate(value);
        if (!createdAt || !createdAt.isValid()) return '';

        const diffDays = dayjs().startOf('day').diff(createdAt.startOf('day'), 'day');

        if (diffDays <= 0) return 'Dodano dzisiaj';
        if (diffDays === 1) return 'Dodano wczoraj';
        if (diffDays <= 13) return `Dodano ${diffDays} dni temu`;

        return `Dodano ${createdAt.format('DD.MM.YYYY')}`;
    };

    const getCreatedAtValue = (source: Record<string, unknown> | undefined): string | number | undefined => {
        const candidates = [
            source?.createdAt,
            source?.created_at,
            source?.createdAtDisplay,
            source?.created_at_display,
            source?.created,
            source?.created_date,
            source?.date,
            source?.createdAtTimestamp,
            source?.created_at_timestamp,
        ];

        return candidates.find((value): value is string | number => typeof value === 'string' || typeof value === 'number') as string | number | undefined;
    };

    const createdAtValue = getCreatedAtValue(job as unknown as Record<string, unknown> | undefined);
    const createdAtLabel = getCreatedAtLabel(createdAtValue);

    let additionals = [];
    if (Array.isArray(job?.jobAgreements) && job?.jobAgreements.length > 0) {
        if(job?.jobAgreements.some(jobAgreement=>jobAgreement.name === JobAgreementsLabels.All)) {
            additionals = [
                ...additionals,
                ...job.jobAgreements.filter(jobAgreement=>jobAgreement.name === JobAgreementsLabels.All)
                    .map(jobAgreement => jobAgreement.name),
            ];
        } else {
            additionals = [
                ...additionals,
                ...job.jobAgreements.map(jobAgreement => jobAgreement.name),
            ];
        }
    }

    if (job.jobTime?.name) {
        additionals = [
            ...additionals,
            job.jobTime.name,
        ];
    }

    return (
        <StyledComponent className="public-layout-jobs-results-list-element">
<Link
  href={
    job?.cbopUrl
      ? job.cbopUrl
      : withVariables(
          Routes.PublicJob,
          { slug: job.slug },
          { jobCitySlug: jobCity?.id }
        )
  }
  prefetch={false}
  passHref
>
  <a
    target={job?.cbopUrl ? "_blank" : undefined}
    rel={job?.cbopUrl ? "noopener noreferrer" : undefined}
  >
    <div className="job-results-logo">
      <img
        className="job-result-logo-img"
        src={
          job.createSource === "cbop"
            ? "/images/jobs/e-praca-logo.svg"
            : imageResizer(job.companyLogo?.imageUrl, { width: 200 }) ||
              "/images/jobs/image-placeholder.svg"
        }
        alt={job.companyName}
        loading="lazy"
        width="155"
        height="155"
      />
    </div>
  </a>
</Link>

            <div className="content">
                <div className="job-results-txt">


                    <a
                        href={ job?.cbopUrl? job.cbopUrl : withVariables(Routes?.PublicJob, { slug: job?.slug }, { jobCitySlug: jobCity?.id })}
                        target={job?.cbopUrl ? "_blank" : undefined}
                        className="job-results-position"
                    >
                        <p
                            className="job-results-name"
                            title={job.name}
                        >{job.name}</p>
                        <p className="job-results-company">{job.companyName}</p>
                        {additionals.length > 0 && (
                            <p className="job-results-contract">
                                {additionals.join(', ')}
                            </p>
                        )}
                    </a>

                    <div className="job-results-details">
                        <div className="job-results-details-txt">
                            {(!isNaN(job.salaryFrom) && job.salaryFrom > 0) && (
                                <p className="job-results-salary">{job.salaryFromTo} pln</p>
                            )}
                            {jobCity.city && (
                                <p className="job-results-place">{jobCity?.companyFullAddress}</p>
                            )}
                        </div>
                        <div className="job-results-action">
                            <SaveButton
                                jobId={job.id}
                                cityId={jobCity?.city.id}
                            />
                            {jobCity?.googleMapLink && (
                                <div onClick={() => userEmail && Tracking.salesManago(
                                    userEmail,
                                    SmEventTypes.Other,
                                    SmEventDescriptons.SeeOnMap,
                                    window.location?.toString(),
                                )}
                                >
                                    <Button
                                        className="job-results-btn"
                                        href={jobCity?.googleMapLink}
                                        {...buttonProps?.road}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="link">
                    <div className="job-results-meta">
                        <div className="job-results-date">
                            {createdAtLabel}
                        </div>
                        <div className="job-results-source">
                            {job?.createSource === 'cbop' ? 'Źródło: oferty.praca.gov.pl' : 'Oferta Praca50+'}
                        </div>
                    </div>
                    <Button
                        className="offer-details"
                        element={ButtonElements.Anchor}
                        style={ButtonStyles.Primary}
                        variant={ButtonVariants.Primary}
                        href={ job?.cbopUrl? job.cbopUrl : withVariables(Routes?.PublicJob, { slug: job?.slug }, { jobCitySlug: jobCity?.id })}
                        target={job?.cbopUrl ? "_blank" : undefined}

                    >
                        Zobacz ofertę
                    </Button>
                </div>
            </div>
        </StyledComponent>
    );
};

export default PublicLayoutJobsResultsListElement;